import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Loader, LoaderStages } from "@/components/Loader";
import { ArrowLeft, Sparkles, TrendingUp, Target, CheckSquare, Clock, ArrowRight, Plus, Check } from "lucide-react";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

const EFFORT_TONE = {
    low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    medium: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    high: "border-red-500/40 bg-red-500/10 text-red-300",
};

/**
 * Stretch Partners — companies that would become a strong fit IF FIRM_A made specific changes.
 * The gap bar visualizes current vs potential fit; required_changes is the actionable roadmap.
 */
export default function StretchPartners() {
    const { id: firmId } = useParams();
    const [firm, setFirm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [partners, setPartners] = useState(null);
    // Per-row promotion state. Map<partnerName, {status: 'pending'|'done'|'error', newFirmId?, err?}>
    const [promoting, setPromoting] = useState({});
    // Bulk save: { active, current, total } while a "Save top N" run is in progress
    const [bulk, setBulk] = useState({ active: false, current: 0, total: 0 });

    useEffect(() => {
        api.get(`/firms/${firmId}`).then(({ data }) => setFirm(data)).catch(() => {});
    }, [firmId]);

    const run = async () => {
        setLoading(true); setError(""); setPartners(null);
        try {
            const { data: enqueued } = await api.post(`/firms/${firmId}/stretch-partners`);
            const result = await pollJob(enqueued.job_id, { timeoutMs: 5 * 60 * 1000 });
            setPartners(result.partners || []);
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setError(rl || (typeof d === "string" ? d : (e.jobError ? e.message : "Stretch search failed")));
        } finally {
            setLoading(false);
        }
    };

    // One-click: research the stretch target with Claude, save the resulting dossier to library, keep the user on this page.
    // After the firm is saved, ALSO fire-and-forget a synergy/best analysis (source firm × newly-saved firm) so
    // by the time the user clicks "View {name}" the Kanban card and pitch deck are already brewing in the background.
    // Returns true on success, false on error/skip — so bulk-save can short-circuit on the first failure.
    const promoteToLibrary = async (name) => {
        if (!name) return false;
        const existing = promoting[name];
        if (existing?.status === "pending" || existing?.status === "done") return existing?.status === "done";
        setPromoting((s) => ({ ...s, [name]: { status: "pending" } }));
        try {
            const { data: enqueued } = await api.post("/research", { firm_name: name });
            const dossier = await pollJob(enqueued.job_id);
            const { data: created } = await api.post("/firms", dossier);
            setPromoting((s) => ({ ...s, [name]: { status: "done", newFirmId: created.id, synergyStatus: "queued" } }));
            // Fire-and-forget the synergy/best job. Do NOT await — bulk-save loop must stay fast.
            // Source firm (URL :id) becomes FIRM_A; newly-saved becomes FIRM_B; Claude picks A's best product.
            api.post("/synergy/best", { firm_a_id: firmId, firm_b_id: created.id })
                .then(async ({ data: enq }) => {
                    try {
                        await pollJob(enq.job_id, { timeoutMs: 4 * 60 * 1000 });
                        setPromoting((s) => ({ ...s, [name]: { ...(s[name] || {}), synergyStatus: "ready" } }));
                    } catch {
                        setPromoting((s) => ({ ...s, [name]: { ...(s[name] || {}), synergyStatus: "failed" } }));
                    }
                })
                .catch(() => {
                    setPromoting((s) => ({ ...s, [name]: { ...(s[name] || {}), synergyStatus: "failed" } }));
                });
            return true;
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setPromoting((s) => ({ ...s, [name]: { status: "error", err: rl || (typeof d === "string" ? d : `Could not save ${name}`) } }));
            return false;
        }
    };

    // Bulk save: sequentially promote the top N partners that aren't already saved or in flight.
    // Sequential = respects the 5-AI-calls-per-minute rate limit (each row triggers one /research call).
    const bulkSaveTopN = async (n = 5) => {
        if (!partners || partners.length === 0 || bulk.active) return;
        const targets = partners
            .filter((p) => promoting[p.name]?.status !== "done")
            .slice(0, n);
        if (targets.length === 0) return;
        setBulk({ active: true, current: 0, total: targets.length });
        for (let i = 0; i < targets.length; i++) {
            setBulk({ active: true, current: i + 1, total: targets.length });
            const ok = await promoteToLibrary(targets[i].name);
            if (!ok) break; // short-circuit on first failure (likely rate-limit or budget)
        }
        setBulk({ active: false, current: 0, total: 0 });
    };

    if (!firm) return <div className="p-12"><Loader label="Loading firm" /></div>;

    return (
        <div className="p-8" data-testid="stretch-page">
            <div className="mb-6 flex items-center justify-between">
                <Link to={`/app/firms/${firmId}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-amber-500" data-testid="back-to-firm">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to {firm.name}
                </Link>
            </div>

            <header className="mb-6 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Stretch partners</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">
                    Where can <span className="text-amber-500">{firm.name}</span> punch above its weight?
                </h1>
                <p className="text-sm text-zinc-500 mt-2 max-w-3xl">
                    Real companies that <strong>aren't</strong> a great fit today — but would become strong partners if {firm.name} makes specific, concrete changes (new region, feature, certification, integration). Sorted by biggest gap = highest leverage.
                </p>
            </header>

            <div className="border border-zinc-800 bg-zinc-950 p-5 mb-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-zinc-300">
                    One AI call. ~10 real companies analyzed. No external API needed.
                </div>
                <button
                    onClick={run}
                    disabled={loading}
                    data-testid="run-stretch-btn"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-5 py-3"
                >
                    <Sparkles className="w-4 h-4" /> {loading ? "Searching…" : partners ? "Refresh" : "Find stretch partners"}
                </button>
            </div>

            {error && <div data-testid="stretch-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">{error}</div>}

            {loading && <div className="border border-zinc-800 bg-zinc-950 p-12"><Loader label="Hunting stretch opportunities" stages={LoaderStages.stretch} /></div>}

            {partners && partners.length === 0 && !loading && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-heading font-bold text-zinc-300 text-lg mb-2">No stretch partners returned</div>
                    <div className="text-sm text-zinc-500">Try refreshing — AI sometimes returns an empty list.</div>
                </div>
            )}

            {partners && partners.length > 0 && (
                <div className="space-y-3" data-testid="stretch-results">
                    {/* Bulk save banner — visible whenever any unsaved rows exist */}
                    {(() => {
                        const unsavedCount = partners.filter((p) => promoting[p.name]?.status !== "done").length;
                        if (unsavedCount === 0) return null;
                        const n = Math.min(5, unsavedCount);
                        return (
                            <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-save-banner">
                                <div className="text-xs text-zinc-300 font-mono">
                                    {bulk.active ? (
                                        <>
                                            <span className="text-amber-400">Saving</span> {bulk.current}/{bulk.total} — researching with Claude (one at a time, may take a few minutes)…
                                        </>
                                    ) : (
                                        <>Promote the top {n} biggest-gap {n === 1 ? "partner" : "partners"} to your library in one go. Each gets a full Claude dossier.</>
                                    )}
                                </div>
                                <button
                                    onClick={() => bulkSaveTopN(5)}
                                    disabled={bulk.active}
                                    data-testid="bulk-save-top-btn"
                                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-wait text-black font-semibold text-[11px] uppercase tracking-wider px-3 py-1.5"
                                >
                                    <Plus className="w-3.5 h-3.5" /> {bulk.active ? `Saving ${bulk.current}/${bulk.total}…` : `Save top ${n} to library`}
                                </button>
                            </div>
                        );
                    })()}
                    {partners.map((p, i) => (
                        <article key={p.name + i} className="border border-zinc-800 bg-zinc-950 p-5" data-testid={`stretch-row-${i}`}>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                {/* Left: header + gap bar */}
                                <div className="md:col-span-7">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div>
                                            <div className="font-heading font-bold text-zinc-50 text-lg">{p.name}</div>
                                            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{p.industry} · {p.region}</div>
                                        </div>
                                        <div className="text-right">
                                            <span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${EFFORT_TONE[p.effort] || EFFORT_TONE.medium}`}>
                                                {p.effort} effort
                                            </span>
                                            <div className="font-mono text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                                                <Clock className="w-2.5 h-2.5" /> ~{p.timeframe_months}mo
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-zinc-300 leading-relaxed mt-2">{p.why_strong_potential}</p>
                                    {/* Gap bar */}
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500 mb-1">
                                            <span>Current fit <span className="text-zinc-200 font-bold">{p.current_fit}</span></span>
                                            <span className="text-emerald-400 font-bold inline-flex items-center gap-1">
                                                <TrendingUp className="w-3 h-3" /> +{p.gap}
                                            </span>
                                            <span>Potential <span className="text-emerald-400 font-bold">{p.potential_fit}</span></span>
                                        </div>
                                        <div className="relative h-2.5 bg-zinc-900 border border-zinc-800" data-testid={`gap-bar-${i}`}>
                                            <div className="absolute inset-y-0 left-0 bg-zinc-600" style={{ width: `${p.current_fit}%` }} />
                                            <div
                                                className="absolute inset-y-0 bg-emerald-500/70"
                                                style={{ left: `${p.current_fit}%`, width: `${Math.max(0, p.potential_fit - p.current_fit)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="font-mono text-[10px] text-amber-500 mt-2">
                                        Potential revenue: <span className="font-bold">{fmtMoney(p.estimated_revenue_usd)}/yr</span>
                                    </div>
                                </div>

                                {/* Right: required changes + promote-to-library */}
                                <div className="md:col-span-5 border-t md:border-t-0 md:border-l border-zinc-800 md:pl-4 pt-3 md:pt-0">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 flex items-center gap-1.5">
                                            <CheckSquare className="w-3 h-3" /> Required steps
                                        </div>
                                        {(() => {
                                            const st = promoting[p.name];
                                            if (st?.status === "done") {
                                                const ss = st.synergyStatus; // queued | ready | failed
                                                const chipTone = ss === "ready"
                                                    ? "text-emerald-400 hover:text-emerald-300"
                                                    : ss === "failed"
                                                    ? "text-zinc-500 hover:text-zinc-400"
                                                    : "text-amber-400 hover:text-amber-300";
                                                const chipLabel = ss === "ready"
                                                    ? "🤝 synergy ready"
                                                    : ss === "failed"
                                                    ? "synergy off"
                                                    : "synergy brewing…";
                                                return (
                                                    <div className="flex flex-col items-end gap-0.5">
                                                        <Link
                                                            to={`/app/firms/${st.newFirmId}`}
                                                            data-testid={`stretch-saved-${i}`}
                                                            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${chipTone}`}
                                                        >
                                                            <Check className="w-3 h-3" /> View {p.name.slice(0, 18)} <ArrowRight className="w-3 h-3" />
                                                        </Link>
                                                        <span className={`font-mono text-[9px] tracking-wider ${chipTone}`} data-testid={`stretch-synergy-status-${i}`}>
                                                            {chipLabel}
                                                        </span>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <button
                                                    onClick={() => promoteToLibrary(p.name)}
                                                    disabled={st?.status === "pending" || bulk.active}
                                                    data-testid={`stretch-save-${i}`}
                                                    title={bulk.active ? "Bulk save in progress…" : `Research ${p.name} with Claude and add it to your library`}
                                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-amber-400 disabled:opacity-50 disabled:cursor-wait border border-zinc-800 hover:border-amber-500 px-2 py-1 transition-colors"
                                                >
                                                    <Plus className="w-3 h-3" /> {st?.status === "pending" ? "Saving…" : "Save to library"}
                                                </button>
                                            );
                                        })()}
                                    </div>
                                    {promoting[p.name]?.status === "error" && (
                                        <div className="mb-2 text-[10px] font-mono text-red-400" data-testid={`stretch-save-error-${i}`}>
                                            {promoting[p.name].err}
                                        </div>
                                    )}
                                    <ol className="space-y-1.5" data-testid={`required-changes-${i}`}>
                                        {p.required_changes.map((c, j) => (
                                            <li key={j} className="text-sm text-zinc-200 leading-snug flex gap-2">
                                                <span className="font-mono text-amber-500 shrink-0">{String(j + 1).padStart(2, "0")}</span>
                                                <span>{c}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {!partners && !loading && !error && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Click "Find stretch partners" to begin</div>
                </div>
            )}
        </div>
    );
}
